import { sm4 } from 'sm-crypto';
import { getStorageCryptoKeyRegistry } from '@/services/cryptoKey';

export type StorageCryptoMode = '' | 'standard' | 'gm';
export type StorageCryptoKeyId = string;

type StorageCryptoSpec = {
  keyId: StorageCryptoKeyId;
  seed?: string;
  sm4Key?: string;
  fixedIv?: string;
  isCurrent?: number;
};

type ActiveStorageCryptoMode = Exclude<StorageCryptoMode, ''>;
type StorageCryptoRegistry = Record<
  ActiveStorageCryptoMode,
  StorageCryptoSpec[]
>;

let registryCache: Promise<StorageCryptoRegistry> | null = null;

const textEncoder = new TextEncoder();

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes)
    .map((item) => item.toString(16).padStart(2, '0'))
    .join('');

const hexToBytes = (hex: string) => {
  const result = new Uint8Array(hex.length / 2);
  for (let i = 0; i < result.length; i += 1) {
    result[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return result;
};

const normalizeCryptoMode = (mode?: string): StorageCryptoMode => {
  if (mode === 'standard' || mode === 'gm') return mode;
  return '';
};

const emptyRegistry = (): StorageCryptoRegistry => ({
  standard: [],
  gm: [],
});

export const loadCryptoRegistry = async () => {
  if (!registryCache) {
    registryCache = getStorageCryptoKeyRegistry().then((items) => {
      const registry = emptyRegistry();
      items.forEach((item) => {
        const mode = normalizeCryptoMode(item.mode);
        if (!mode || !item.keyId || !item.keyMaterial) return;
        registry[mode].push({
          keyId: item.keyId,
          seed: mode === 'standard' ? item.keyMaterial : undefined,
          sm4Key: mode === 'gm' ? item.keyMaterial : undefined,
          fixedIv: item.ivMaterial || undefined,
          isCurrent: Number(item.isCurrent || 0),
        });
      });

      // 提取当前激活的国密 GM 密钥并存入 localStorage 供传输加密使用
      const activeGm =
        registry.gm.find((item) => item.isCurrent === 1) || registry.gm[0];
      if (activeGm) {
        localStorage.setItem('transmission_sm4_key_id', activeGm.keyId);
        localStorage.setItem('transmission_sm4_key', activeGm.sm4Key || '');
        localStorage.setItem('transmission_sm4_iv', activeGm.fixedIv || '');
      } else {
        localStorage.removeItem('transmission_sm4_key_id');
        localStorage.removeItem('transmission_sm4_key');
        localStorage.removeItem('transmission_sm4_iv');
      }

      return registry;
    });
  }
  return registryCache;
};

export const clearStorageCryptoKeyCache = () => {
  registryCache = null;
};

export const refreshCryptoRegistry = async () => {
  clearStorageCryptoKeyCache();
  return loadCryptoRegistry();
};

const getCurrentSpec = async (mode: ActiveStorageCryptoMode) => {
  const registry = await loadCryptoRegistry();
  const list = registry[mode] || [];
  const spec = list.find((item) => item.isCurrent === 1) || list[0];
  if (!spec) {
    throw new Error(`未配置${mode}加密密钥，请先在加密密钥管理中启用密钥`);
  }
  return spec;
};

const resolveSpec = async (mode: ActiveStorageCryptoMode, keyId?: string) => {
  const registry = await loadCryptoRegistry();
  const list = registry[mode] || [];
  const normalizedKeyId = String(keyId || '').trim();
  if (normalizedKeyId) {
    const found = list.find((item) => item.keyId === normalizedKeyId);
    if (found) return found;
    throw new Error(`未找到加密密钥版本: ${normalizedKeyId}`);
  }
  return getCurrentSpec(mode);
};

const importStandardKey = async (seed: string) => {
  const raw = await globalThis.crypto.subtle.digest(
    'SHA-256',
    textEncoder.encode(seed),
  );
  return globalThis.crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
};

export const encryptFileForStorage = async (
  file: File,
  isSensitive?: boolean,
  cryptoMode?: string,
  keyId?: string,
) => {
  // 不希望对文件内容加密，只管传输方式的加密。在此直接返回原始明文文件。
  return {
    file,
    isSensitive: 0,
    cryptoMode: '',
    cryptoMeta: '',
    originalFileSize: 0,
  };
};

export const decryptBlobFromStorage = async (
  blob: Blob,
  isSensitive?: boolean,
  cryptoMode?: string,
  cryptoMeta?: string,
) => {
  const mode = normalizeCryptoMode(cryptoMode);
  if (!isSensitive || !mode) return blob;

  const input = await blob.arrayBuffer();
  const meta = cryptoMeta ? JSON.parse(cryptoMeta) : {};
  const spec = await resolveSpec(mode, String(meta.keyId || ''));

  if (mode === 'standard') {
    const ivHex = String(meta.iv || '');
    if (!ivHex) return blob;
    const key = await importStandardKey(spec.seed || '');
    const decrypted = await globalThis.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: hexToBytes(ivHex) },
      key,
      input,
    );
    return new Blob([decrypted]);
  }

  const sm4Key = spec.sm4Key || '';
  const ivHex = String(meta.iv || spec.fixedIv || '');
  const decryptedBytes = sm4.decrypt(
    Array.from(new Uint8Array(input)),
    sm4Key,
    {
      mode: 'cbc',
      iv: ivHex,
      padding: 'pkcs#7',
      output: 'array',
    },
  );

  return new Blob([new Uint8Array(decryptedBytes)]);
};
